import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaCalendarAlt, FaCheck, FaTimes } from "react-icons/fa";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toYmd(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function formatDisplay(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return ymd;
  return `${d}/${m}/${y}`;
}

function getPresets() {
  const today = startOfDay(new Date());
  const last7 = startOfDay(new Date(today));
  last7.setDate(last7.getDate() - 6);
  const last30 = startOfDay(new Date(today));
  last30.setDate(last30.getDate() - 29);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return [
    { id: "today", label: "Today", from: toYmd(today), to: toYmd(today) },
    { id: "7d", label: "Last 7 days", from: toYmd(last7), to: toYmd(today) },
    { id: "30d", label: "Last 30 days", from: toYmd(last30), to: toYmd(today) },
    { id: "month", label: "This month", from: toYmd(monthStart), to: toYmd(today) },
  ];
}

/**
 * Custom date range picker with preset chips + From/To fields.
 * Panel is portaled to document.body so it is not clipped by card overflow.
 */
export default function DateRangePicker({
  fromDate = "",
  toDate = "",
  onChange,
  disabled = false,
  label = "Date range",
}) {
  const presets = useMemo(() => getPresets(), []);
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(fromDate || "");
  const [draftTo, setDraftTo] = useState(toDate || "");
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 360 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (open) {
      setDraftFrom(fromDate || "");
      setDraftTo(toDate || "");
    }
  }, [open, fromDate, toDate]);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.min(360, Math.max(280, window.innerWidth - 24));
    let left = rect.left;
    if (left + width > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - width - 12);
    }
    let top = rect.bottom + 6;
    const estimatedHeight = 320;
    if (top + estimatedHeight > window.innerHeight - 12 && rect.top > estimatedHeight) {
      top = Math.max(12, rect.top - estimatedHeight - 6);
    }
    setPanelPos({ top, left, width });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      const inTrigger = rootRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const activePresetId = useMemo(() => {
    if (!fromDate || !toDate) return "";
    const match = presets.find((p) => p.from === fromDate && p.to === toDate);
    return match?.id || "";
  }, [fromDate, toDate, presets]);

  const summary = useMemo(() => {
    if (!fromDate && !toDate) return "All dates";
    if (fromDate && toDate) {
      const preset = presets.find((p) => p.from === fromDate && p.to === toDate);
      if (preset) return preset.label;
      return `${formatDisplay(fromDate)} – ${formatDisplay(toDate)}`;
    }
    return "Incomplete range";
  }, [fromDate, toDate, presets]);

  const draftError = useMemo(() => {
    const f = draftFrom.trim();
    const t = draftTo.trim();
    if (!f && !t) return "";
    if (!f || !t) return "Both from and to dates are required";
    if (f > t) return "From date cannot be after to date";
    return "";
  }, [draftFrom, draftTo]);

  const commit = (from, to) => {
    onChange?.({ from_date: from || "", to_date: to || "" });
    setOpen(false);
  };

  const applyPreset = (preset) => {
    setDraftFrom(preset.from);
    setDraftTo(preset.to);
    commit(preset.from, preset.to);
  };

  const applyDraft = () => {
    if (draftError) return;
    commit(draftFrom.trim(), draftTo.trim());
  };

  const clearRange = () => {
    setDraftFrom("");
    setDraftTo("");
    commit("", "");
  };

  const panel = open
    ? createPortal(
        <div
          ref={panelRef}
          className="drp-panel shadow"
          style={{
            position: "fixed",
            top: panelPos.top,
            left: panelPos.left,
            width: panelPos.width,
            zIndex: 2000,
          }}
        >
          <div className="drp-presets">
            <div className="drp-section-label">Quick select</div>
            <div className="d-flex flex-wrap gap-2">
              {presets.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`btn btn-sm drp-chip ${activePresetId === p.id ? "drp-chip-active" : ""}`}
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="drp-divider" />

          <div className="drp-section-label">Custom range</div>
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label small text-muted mb-1">From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={draftFrom}
                max={draftTo || undefined}
                onChange={(e) => setDraftFrom(e.target.value)}
              />
            </div>
            <div className="col-6">
              <label className="form-label small text-muted mb-1">To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={draftTo}
                min={draftFrom || undefined}
                onChange={(e) => setDraftTo(e.target.value)}
              />
            </div>
          </div>

          {draftError ? (
            <div className="small text-danger mt-2">{draftError}</div>
          ) : (
            <div className="small text-muted mt-2">
              Leave both empty for all dates, or set both to filter.
            </div>
          )}

          <div className="d-flex gap-2 justify-content-end mt-3">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearRange}>
              Clear
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
              onClick={applyDraft}
              disabled={!!draftError}
            >
              <FaCheck size={11} />
              Apply
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="drp-root" ref={rootRef}>
      {label ? (
        <label className="form-label small text-muted mb-1 d-block">
          <FaCalendarAlt className="me-1" />
          {label}
        </label>
      ) : null}

      <button
        ref={triggerRef}
        type="button"
        className={`btn btn-sm drp-trigger ${open ? "drp-trigger-open" : ""} ${
          fromDate || toDate ? "drp-trigger-active" : ""
        }`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={open}
      >
        <FaCalendarAlt className="me-2 flex-shrink-0" />
        <span className="drp-trigger-text text-truncate">{summary}</span>
        {(fromDate || toDate) && (
          <span
            className="drp-clear-inline"
            role="button"
            tabIndex={-1}
            title="Clear dates"
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) clearRange();
            }}
          >
            <FaTimes size={10} />
          </span>
        )}
      </button>

      {panel}

      <style>{`
        .drp-root {
          position: relative;
          min-width: 220px;
        }
        .drp-trigger {
          width: 100%;
          display: inline-flex;
          align-items: center;
          justify-content: flex-start;
          gap: 4px;
          padding: 0.4rem 0.65rem;
          border-radius: var(--tb-radius-sm, 4px);
          border: 1px solid var(--tb-line-strong, #dee2e6);
          background: var(--tb-bg-elevated, #fff);
          color: var(--tb-ink, #212529);
          font-weight: 500;
          text-align: left;
        }
        .drp-trigger:hover:not(:disabled) {
          border-color: var(--tb-accent, #0f766e);
          background: var(--tb-bg-soft, #f8fafc);
        }
        .drp-trigger-open,
        .drp-trigger-active {
          border-color: var(--tb-accent, #0f766e);
          box-shadow: 0 0 0 0.15rem var(--tb-accent-glow, rgba(15, 118, 110, 0.15));
        }
        .drp-trigger-text {
          flex: 1;
          min-width: 0;
        }
        .drp-clear-inline {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--tb-bg-soft, #e9ecef);
          color: var(--tb-muted, #6c757d);
          flex-shrink: 0;
        }
        .drp-clear-inline:hover {
          background: var(--tb-line, #dee2e6);
          color: var(--tb-ink, #212529);
        }
        .drp-panel {
          background: var(--tb-bg-elevated, #fff);
          border: 1px solid var(--tb-line, #e5e7eb);
          border-radius: var(--tb-radius, 6px);
          padding: 14px;
          color: var(--tb-ink, #212529);
          box-shadow: var(--tb-shadow-lg, 0 8px 24px rgba(0,0,0,0.12));
        }
        .drp-section-label {
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--tb-muted, #6b7280);
          margin-bottom: 8px;
        }
        .drp-divider {
          height: 1px;
          background: var(--tb-line, #eef2f7);
          margin: 12px 0;
        }
        .drp-chip {
          border-radius: 999px;
          border: 1px solid var(--tb-line-strong, #dee2e6);
          background: var(--tb-bg-soft, #f8fafc);
          color: var(--tb-ink-soft, #374151);
          font-weight: 500;
          font-size: 0.75rem;
          padding: 0.3rem 0.7rem;
        }
        .drp-chip:hover {
          border-color: var(--tb-accent, #0f766e);
          color: var(--tb-accent, #0f766e);
        }
        .drp-chip-active {
          background: var(--tb-accent, #0f766e);
          border-color: var(--tb-accent, #0f766e);
          color: #fff;
        }
      `}</style>
    </div>
  );
}
